import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { appDispatch, RootState } from 'store/store';
import { getSingleTask, searchTasks, updateTask } from 'store/tasks/tasksSlice';
import {
  IAssignmentHistory,
  IAssignmentView,
  ITask,
} from 'store/tasks/tasksType';
import * as taskManipulate from '../../store/tasks/taskManipulate';
import SanitizedHTML from 'react-sanitized-html';
import styled from 'styled-components';
import {
  black,
  blue,
  border1,
  exactWhite,
  green,
  lightGray,
  orange,
  purple,
  red,
} from 'utils/style/colors';
import ScheduleDurationPoints from './ScheduleDurationPoints';
import Button from 'components/Button';
import { cloneDeep, flatMap } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  endOfDate,
  monthIndexToTimestamp,
  smartDateDuration,
  smartDateString,
  startOfDate,
} from 'utils/date';
import { getJMonthIndex } from 'components/Chart/Bucket';
import i18n from 'i18n';
import moment from 'moment-jalaali';
import tzMoment from 'moment-timezone';
import { convertDigitToProperFormat } from 'utils/number';
import ButtonGroup from 'components/ButtonGroup';
import ProgressLineChart from 'components/Chart/ProgressLineChart';
import SquereProgress from './SquereProgress';
import { IUserOption } from 'components/UserSelector/UserSelector';
import TaskUserList from 'components/UserSelector/TaskUserList';
import {
  MultiDatePicker,
  MultiDatePicker_Range,
} from 'components/MyDatePicker/MultiDatePicker';
import { buildAndComputeSchedule, IDuration, NodeRelations } from './scheduler';
import { SINGLE_DAY_BASE_ON_TIME_STAMP } from 'components/FileManager/ItemsViewer/Survey/SurveyResponseViewer/ResponseItem';
import LoadingCircleIndicator from 'components/LoadingCircleIndicator';
import APopover from 'components/APopover';
import Popover from 'components/Popover';
import Icon from 'components/Icon';
import UserMixpanel from 'utils/mixpanel/MixpanelClass';
import ItemTask from 'containers/Profile/Panels/DocumentManager/ItemTask';
import BlurModal from 'containers/Profile/Panels/DocumentManager/BlurModal';
import MoreInfoModalContent from './MoreInfoModalContent';
import { withTitle } from 'components/withTitle';
import { saveAs } from 'file-saver';

const borderColor: Record<number, string> = {
  0: green,
  1: red,
  2: purple,
  3: blue,
  4: orange,
};

const backgroundColor: Record<number, string> = {
  0: '#00800017',
  1: '#ff000017',
  2: '#80008017',
  3: '#0000ff17',
  4: '#ffa60030',
};

const SmartScheduleWrapper = styled.div`
  width: 100%;
  padding: 2rem auto;
  position: relative;
`;

const TreeWrapper = styled.div`
  max-height: 50vh;
  overflow: hidden auto;
  display: grid;
  grid-template-columns: repeat(2, 50%);
  position: relative;
  margin: 2.5rem 0 1rem;
  padding: 0 0.75rem;
`;

const TimeRangeVirticalLineWrapper = styled.div`
  width: 50%;
  position: absolute;
  min-height: 100%;
  max-height: ${(props) => `${props.counter * 45}px`};
  overflow: hidden;
  left: 0;
  top: 0;
`;

const TR = styled.tr`
  width: 100%;
  display: flex;
  position: sticky;
  /* padding-bottom: 1rem; */
`;

const TH = styled.th`
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.75rem !important;
  width: calc(100% / ${(props) => props.num});
  border-inline-end: 1px dashed ${lightGray};
  font-weight: 300;
  text-align: center !important;

  & > div {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:last-child {
    border-inline-end: none;
  }
`;

const TD = styled.td`
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.8rem !important;
  width: calc(100% / ${(props) => props.num});
  /* border-inline-end: 1px dashed ${lightGray}; */
  border-radius: 0;
  height: 45px;
  z-index: 3;

  /* &:last-child {
    border-inline-end: none;
  } */
`;

const InputWrapper = styled.div`
  width: 3rem;
  max-width: 4rem;
  min-width: 4rem;
  max-height: 45px;
  padding-inline-end: 1rem;

  & > label {
    font-size: 0.6rem !important;
  }

  & > input {
    margin: 0 !important;
    border: ${border1} !important;
    outline: none !important;
    border-radius: 0.5rem !important;
    height: 1.75rem !important;

    &:focus,
    &:selected {
      outline: none !important;
      box-shadow: none !important;
      border: ${border1} !important;
    }
  }
`;

const TaskRowContainer = styled.div`
  display: flex;
  margin-inline-start: ${(props) => `${props.level * 24}px`};
  position: sticky;
  top: ${(props) => `${props.level * 42}px`};
  opacity: ${(props) =>
    !props.dueDate ? '1' : props.hasAcheivedToDueDate ? '0.4' : '1'};
  z-index: ${(props) => `${1000 - props.level}`};
  gap: 0.5rem;
  align-items: 'center';
  background: ${exactWhite};

  & i {
    visibility: ${(props) =>
      props.isActiveOverrideTiming ? 'visible' : 'hidden'};
  }

  &:hover i {
    visibility: visible;
  }
`;

const TaskTitleRow = styled.div`
  border-inline-start: ${(props) =>
    props.level ? `2px solid ${borderColor[props.level % 5]}` : 'none'};

  padding: 0.5rem 0.75rem;
  margin: 6px 0;
  display: inline-block;
  background: ${(props) => backgroundColor[props.level % 5]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
`;

const CustomizedTaskContainer = styled.div`
  margin-inline-start: auto;
  width: 6rem;
`;

const ProgressWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 1rem 0;
`;

const DependantTasksInformationContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: ${border1};
  border-radius: 0.5rem;
  color: ${exactWhite};
  background-color: ${orange};
  padding: 0.5rem 0.75rem;
`;

const MoreBtn = styled.button`
  padding: 0.1rem 0.25rem;
  margin-inline-start: auto;
  color: ${black};
  border: 1px solid ${black};
  border-radius: 0.25rem;

  &:hover {
    color: ${exactWhite};
    background: ${black};
  }

  &:active {
    color: ${exactWhite} !important;
    background: ${black} !important;
  }
`;

export type IScheduleActiveTab = 'progressDiagram' | 'timetable';

interface ISmartScheduleProps {
  task: ITask;
  companyId: number | null;
  onCloseModal: () => void;
  activeTab?: IScheduleActiveTab;
}

export interface IDiagramTimestampRange {
  startTime: number;
  dueTime: number;
}

export type TimelinePoint = {
  ratio: number;
  type: 'base-start' | 'task-start' | 'task-end' | 'base-end';
  // message: string;
};

export type TNodeWeight = {
  nodeWeight: number;
};

export type TNodeWeightAssignee = {
  assingneeNodeWeight: number;
};

const ROOT_ID = -1;

const stripHtmlFromString = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent || '').trim();
};

type TimetableTreeEntry = {
  task: ITask;
  level: number;
  isLast: boolean;
};

const flattenTasksInTreeOrder = (
  rootTask: ITask,
  childrenMap: Record<number, ITask[]>,
  level = 0,
  isLast = true
): TimetableTreeEntry[] => {
  const children = childrenMap[rootTask.id] ?? [];
  const result: TimetableTreeEntry[] = [{ task: rootTask, level, isLast }];

  children.forEach((child, index) => {
    result.push(
      ...flattenTasksInTreeOrder(
        child,
        childrenMap,
        level + 1,
        index === children.length - 1
      )
    );
  });

  return result;
};

const formatTaskNameForExport = (title: string, level: number) => {
  const cleanTitle = stripHtmlFromString(title);
  if (level === 0) {
    return cleanTitle;
  }

  const indent = '    '.repeat(Math.max(0, level - 1));
  return `${indent}${cleanTitle}`;
};

const setWorksheetColumnWidths = (worksheet: {
  columns?: Array<{ eachCell?: any; width?: number }>;
}) => {
  worksheet.columns?.forEach((column) => {
    let maxLength = 10;
    column.eachCell?.({ includeEmpty: true }, (cell: { value?: unknown }) => {
      const cellLength = cell.value ? String(cell.value).length : 0;
      maxLength = Math.max(maxLength, cellLength);
    });
    column.width = Math.min(80, maxLength + 3);
  });
};

type TaskAssigneeExportData = {
  firstDateTimestamp?: number;
  lastDateTimestamp?: number;
  progress: number;
};

const getActualDateRangeFromAssigneeData = (
  assigneeData: TaskAssigneeExportData[]
): { actualStart: string; actualEnd: string } => {
  const formatTimestamp = (timestamp: number) =>
    smartDateString(timestamp, true, true, true);

  const firstTimestamps = assigneeData
    .map((item) => item.firstDateTimestamp)
    .filter((timestamp): timestamp is number => Boolean(timestamp));

  const lastTimestamps = assigneeData
    .map((item) => item.lastDateTimestamp)
    .filter((timestamp): timestamp is number => Boolean(timestamp));

  return {
    actualStart: firstTimestamps.length
      ? formatTimestamp(Math.min(...firstTimestamps))
      : '',
    actualEnd: lastTimestamps.length
      ? formatTimestamp(Math.max(...lastTimestamps))
      : '',
  };
};

const formatMeanAssigneeProgress = (
  assigneeData: TaskAssigneeExportData[]
): string => {
  if (!assigneeData.length) return '';
  const mean =
    assigneeData.reduce((sum, item) => sum + item.progress, 0) /
    assigneeData.length;
  return `${convertDigitToProperFormat(Math.round(mean))}%`;
};

const getMaxAssigneeProgressBeforeDate = (
  task: ITask,
  date: number
): number => {
  const history = (task.assignmentHistoryList || []).filter(
    (item) => item.state !== 'Delete' && Number(item.updateDate) <= date
  );

  const assignees = Object.values(task.assignee || {});
  const userIds = new Set<number>([
    ...assignees.map((assignment) => assignment.userId),
    ...history.map((item) => item.userId),
  ]);

  if (!userIds.size) {
    return 0;
  }

  const historyByUserId = history.reduce<Record<number, IAssignmentHistory[]>>(
    (acc, item) => {
      if (!acc[item.userId]) {
        acc[item.userId] = [];
      }
      acc[item.userId].push(item);
      return acc;
    },
    {}
  );

  const assigneeProgresses = [...userIds].map((userId) => {
    const entries = historyByUserId[userId];
    if (!entries?.length) {
      if (!(task.assignmentHistoryList || []).length) {
        const assignment = assignees.find(
          (assignee) => assignee.userId === userId
        );
        return assignment?.progress ?? 0;
      }
      return 0;
    }

    const sorted = [...entries].sort(
      (a, b) => Number(a.updateDate) - Number(b.updateDate)
    );
    return sorted[sorted.length - 1]?.progress ?? 0;
  });

  return Math.max(...assigneeProgresses, 0);
};

export const computeTaskTreeProgressByDate = (
  rootTask: ITask,
  childrenMap: Record<number, ITask[]>,
  date: number
): Record<number, number> => {
  const progressByTaskId: Record<number, number> = {};

  const dfs = (currentTask: ITask): number => {
    const children = childrenMap[currentTask.id] ?? [];
    const isLeaf =
      !children.length || currentTask.taskProgressType?.main === 'Assignee';

    let progress: number;
    if (isLeaf) {
      progress = getMaxAssigneeProgressBeforeDate(currentTask, date);
    } else {
      const childProgresses = children.map((child) => dfs(child));
      progress = childProgresses.length
        ? childProgresses.reduce((sum, value) => sum + value, 0) /
          childProgresses.length
        : 0;
    }

    progressByTaskId[currentTask.id] = progress;
    return progress;
  };

  dfs(rootTask);
  return progressByTaskId;
};

const getTaskAssigneeExportData = (task: ITask): TaskAssigneeExportData[] => {
  const history = (task.assignmentHistoryList || []).filter(
    (item) => item.state !== 'Delete'
  );

  if (!history.length) {
    return Object.values(task.assignee || {}).map((assignment) => ({
      progress: assignment.progress ?? 0,
    }));
  }

  const historyByUserId = history.reduce<Record<number, IAssignmentHistory[]>>(
    (acc, item) => {
      if (!acc[item.userId]) {
        acc[item.userId] = [];
      }
      acc[item.userId].push(item);
      return acc;
    },
    {}
  );

  return Object.values(historyByUserId).map((entries) => {
    const sorted = [...entries].sort(
      (a, b) => Number(a.updateDate) - Number(b.updateDate)
    );
    const firstWithPositiveProgress = sorted.find(
      (entry) => Number(entry.progress) > 0
    );
    const last = sorted[sorted.length - 1];

    return {
      firstDateTimestamp: firstWithPositiveProgress
        ? Number(firstWithPositiveProgress.updateDate)
        : undefined,
      lastDateTimestamp: Number(last.updateDate),
      progress: last.progress ?? 0,
    };
  });
};

const SmartSchedule: FC<ISmartScheduleProps> = ({
  task,
  companyId,
  onCloseModal,
  activeTab,
}) => {
  const { t } = useTranslation('taskManager');

  const buttonItems = {
    timetable: t('timetable'),
    progressDiagram: t('progressDiagram'),
  };
  const allStoreTasks = useSelector((state: RootState) => state.tasks.data);
  const users = useSelector((state: RootState) => state.users);
  const company = useSelector(
    (state: RootState) => state.companies.data?.[companyId || '']
  );
  const [saving, setSaving] = useState<boolean>(false);

  const [customizeTiming, setCustomizeTiming] = useState<boolean>(false);
  const [_, setShowPreview] = useState<boolean>(false);
  const [fetchingData, setFetchingData] = useState<boolean>(false);
  const [tasksDuration, setTasksDuration] = useState<IDuration>({});
  const [overrideTime, setOverrideTime] = useState<IDuration>({});

  const [moreInfoModalIsOpen, setMoreInfoModalIsOpen] =
    useState<boolean>(false);

  const [customizedBaseDate, setCustomizedBaseDate] = useState<number | null>(
    new Date().getTime()
  );

  const [hierarchyTasks, setHierarchyTasks] =
    useState<Record<number, ITask[]>>();
  const [selectedTab, setSelectedTab] = useState<string>(
    activeTab ?? 'timetable'
  );
  const [diagramTimestampRange, setDiagramTimestampRange] =
    useState<IDiagramTimestampRange>();

  const [tasksInStackId, setTasksInStackId] = useState<null | {
    taskId: number;
    zIndex: number;
  }>(null);

  const setStartOfADateBasedOnTimeZone = (timestamp: number) => {
    const local = tzMoment(timestamp);
    const time = local.tz(company?.timeZone ?? 'Asia/Tehran');
    time.set({ hour: 0, minute: 0, second: 0, millisecond: 0 });
    return time.valueOf();
  };

  const handleCloseTaskStackModal = () => {
    setTasksInStackId(null);
  };

  const clamp = (v: number, min = 0, max = 1) =>
    Math.min(max, Math.max(min, v));

  const getTaskRangeRatio = (task: ITask, range: IDiagramTimestampRange) => {
    if (
      typeof task.startDate !== 'number' ||
      typeof task.dueDate !== 'number'
    ) {
      return null;
    }
    const baseStart = range.startTime;
    const baseEnd = range.dueTime;
    const baseDuration = baseEnd - baseStart;
    const startRatio = clamp((task.startDate - baseStart) / baseDuration);
    const endRatio = clamp((task.dueDate - baseStart) / baseDuration);
    if (endRatio <= startRatio) return null;

    return {
      right: startRatio,
      width: endRatio - startRatio,
    };
  };

  const getMonthName = (monthIndx: number) => {
    switch (monthIndx) {
      case 1:
        return i18n.t('1stMonth', { ns: 'survey' }) as string;
      case 2:
        return i18n.t('2ndMonth', { ns: 'survey' }) as string;
      case 3:
        return i18n.t('3rdMonth', { ns: 'survey' }) as string;
      case 4:
        return i18n.t('4thMonth', { ns: 'survey' }) as string;
      case 5:
        return i18n.t('5thMonth', { ns: 'survey' }) as string;
      case 6:
        return i18n.t('6thMonth', { ns: 'survey' }) as string;
      case 7:
        return i18n.t('7thMonth', { ns: 'survey' }) as string;
      case 8:
        return i18n.t('8thMonth', { ns: 'survey' }) as string;
      case 9:
        return i18n.t('9thMonth', { ns: 'survey' }) as string;
      case 10:
        return i18n.t('10thMonth', { ns: 'survey' }) as string;
      case 11:
        return i18n.t('11thMonth', { ns: 'survey' }) as string;
      case 12:
        return i18n.t('12thMonth', { ns: 'survey' }) as string;
      default:
        return i18n.t('1stMonth', { ns: 'survey' }) as string;
    }
  };

  const getAllTasksRecursively = async (
    parentTaskId: number,
    acc: ITask[]
  ): Promise<ITask[]> => {
    if (acc.some((t) => t.id === parentTaskId)) return acc;

    if (
      allStoreTasks[parentTaskId] &&
      allStoreTasks[parentTaskId]?.progressHistory?.mainProgress
    ) {
      acc.push(allStoreTasks[parentTaskId]);
    } else {
      const res = await appDispatch(
        getSingleTask({
          taskId: parentTaskId,
          includeProgressHistory: true,
          includeAssignmentHistory: true,
        })
      );
      acc.push(res.task);
    }

    const res = await appDispatch(
      searchTasks({
        params: {
          parentId: parentTaskId,
          companyId: companyId!,
          includeProgressHistory: true,
        },
      })
    );

    for (const childTask of res.tasks) {
      await getAllTasksRecursively(childTask.id, acc);
    }

    return acc;
  };

  const buildChildrenMap = (tasks: ITask[], rootTask: ITask) => {
    const map: Record<number, ITask[]> = {
      [ROOT_ID]: [rootTask],
    };

    const sortedTasks = tasks.sort((a: ITask, b: ITask) => a.order - b.order);

    for (const task of sortedTasks) {
      const parentId =
        task.parentId !== null && task.parentId !== undefined
          ? task.parentId
          : ROOT_ID;

      if (!map[parentId]) {
        map[parentId] = [];
      }

      map[parentId].push(task);
    }

    return map;
  };

  const handleGenerateAssigneeValues = (assignee: {
    [key: number]: IAssignmentView;
  }) => {
    const assigneeValues = Object.values(assignee || {}).reduce(
      (list: IUserOption[], value) => {
        const item: IUserOption = {
          userId: value.userId,
          name: users.map?.[value.userId]?.name || '',
          roles: [],
          progressDescription: value.progressDescription,
          progress: value.progress,
          optionType: 'user',
          status: value.status ? value.status : 'notStarted',
          relatedRoleId: value.properties?.roleId,
          relatedRoleName: value.properties?.roleName,
        };
        list.push(item);
        return list;
      },
      []
    );
    return assigneeValues;
  };

  const handleChangeTaskDuration = (taskId: string, duration: number) => {
    if (Object.keys(tasksDuration || {}).includes(taskId)) {
      setTasksDuration(
        Object.fromEntries(
          Object.entries(tasksDuration || {}).map(([key, val]) =>
            key === taskId ? [key, duration] : [key, val]
          )
        )
      );
    } else {
      setTasksDuration({ ...tasksDuration, [taskId]: duration });
    }
  };

  const handleClickOnTask = (task: ITask, zIndex: number) => {
    const mx = UserMixpanel.getInstance();
    mx.visitedTasksCountHandler();
    if (Number(task.id)) {
      mx.handleSetVisitedTaskCountByTemplates(task.taskName);
    }
    setTasksInStackId({ taskId: Number(task.id), zIndex: zIndex });
  };

  const handleChangeOverrideTime = (
    taskId: string,
    overrideTime: number | null
  ) => {
    setOverrideTime((prevVal) => ({ ...prevVal, [taskId]: overrideTime }));
  };

  const handleOpenMoreInfoModal = () => {
    setMoreInfoModalIsOpen(true);
  };

  const formatTaskDateForExport = (task: ITask, date?: number) => {
    if (!date) return '';
    return smartDateString(date, task.dateAndTime, true, true);
  };

  const handleExportTimetableExcel = async () => {
    const rootTask = hierarchyTasks?.[ROOT_ID]?.[0];
    if (!rootTask || !hierarchyTasks) return;

    const treeEntries = flattenTasksInTreeOrder(rootTask, hierarchyTasks);
    const assigneeDataPerTask = treeEntries.map(({ task: item }) =>
      getTaskAssigneeExportData(item)
    );

    const headerValues = [
      t('task'),
      t('expectedStartDate'),
      t('expectedDueDate'),
      t('actualStartDate'),
      t('actualEndDate'),
      t('averageAssignmentProgress'),
    ];

    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(t('timetable') as string, {
      views: [
        {
          state: 'frozen',
          xSplit: 6,
          ySplit: 1,
          topLeftCell: 'G2',
          rightToLeft: true,
        },
      ],
    });

    const headerRow = worksheet.addRow(headerValues);
    headerRow.height = 22;
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = {
        horizontal: 'right',
        vertical: 'middle',
        wrapText: true,
        readingOrder: 'rtl',
      };
    });

    treeEntries.forEach((entry, index) => {
      const { task: item, level } = entry;
      const assigneeData = assigneeDataPerTask[index];
      const { actualStart, actualEnd } =
        getActualDateRangeFromAssigneeData(assigneeData);

      const rowValues = [
        formatTaskNameForExport(item.title, level),
        formatTaskDateForExport(item, item.startDate),
        formatTaskDateForExport(item, item.dueDate),
        actualStart,
        actualEnd,
        formatMeanAssigneeProgress(assigneeData),
      ];

      const row = worksheet.addRow(rowValues);
      row.height = 22;

      row.eachCell((cell, colNumber) => {
        cell.alignment = {
          horizontal: 'right',
          vertical: 'middle',
          wrapText: true,
          readingOrder: 'rtl',
          indent: colNumber === 1 ? level * 2 : 0,
        };
      });
    });

    setWorksheetColumnWidths(worksheet);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const fileName = `${stripHtmlFromString(task.title) || 'schedule'}-${t(
      'timetable'
    )}.xlsx`.replace(/[/\\?%*:|"<>]/g, '-');
    saveAs(blob, fileName);
  };

  const renderTaskTree = (
    task: ITask,
    childrenMap: Record<number, ITask[]>,
    level = 0,
    dueDate = 0,
    zIndex = 0,
    progressByTaskId?: Record<number, number>
  ) => {
    const hasAcheivedToDueDate = (task?.dueDate || 0) > dueDate;
    const resolvedProgressByTaskId =
      progressByTaskId ??
      (dueDate
        ? computeTaskTreeProgressByDate(
            childrenMap[ROOT_ID]?.[0] ?? task,
            childrenMap,
            dueDate
          )
        : undefined);
    const taskAssignmentProgressHistoryBeforeDueDate =
      resolvedProgressByTaskId?.[task.id] ?? 0;

    const hasChild = childrenMap[task.id]?.length ? true : false;

    return (
      <div key={task.id} className="relative">
        <TaskRowContainer
          level={level}
          dueDate={dueDate}
          hasAcheivedToDueDate={hasAcheivedToDueDate}
          isActiveOverrideTiming={overrideTime?.[task.id?.toString()] ?? false}
        >
          {dueDate ? (
            <div className="flex gap-3 items-center">
              <SquereProgress
                isProgressive={
                  task?.taskProgressType?.main === 'Child' ||
                  task?.type === 'progressive'
                    ? true
                    : false
                }
                progress={Number(
                  taskAssignmentProgressHistoryBeforeDueDate?.toFixed(0)
                )}
                size={30}
                showOnlyPercent={true}
              />
            </div>
          ) : (
            <></>
          )}
          <TaskTitleRow
            level={level}
            onClick={() => handleClickOnTask(task, zIndex)}
          >
            <APopover
              trigger="hover"
              target={
                <div>
                  <SanitizedHTML
                    allowedAttributes={{ span: ['style'] }}
                    html={task.title}
                  />
                </div>
              }
            >
              <div
                style={{
                  background: exactWhite,
                  padding: '0.5rem',
                }}
              >
                <SanitizedHTML
                  allowedAttributes={{ span: ['style'] }}
                  html={task.title}
                />
              </div>
            </APopover>
          </TaskTitleRow>
          {dueDate ? (
            <>
              {' '}
              <div
                style={{
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {task?.startDate && task?.dueDate ? (
                  smartDateDuration(
                    task.startDate,
                    task.dueDate,
                    task.dateAndTime,
                    true,
                    true
                  )
                ) : task?.startDate ? (
                  smartDateString(task?.startDate, task.dateAndTime, true, true)
                ) : task?.dueDate ? (
                  smartDateString(task?.dueDate, task.dateAndTime, true, true)
                ) : (
                  <></>
                )}
              </div>
              <div style={{ marginInlineStart: 'auto' }}>
                <TaskUserList
                  trigger="hover"
                  type={task.type}
                  task={task}
                  canChangeProgress={false}
                  canChangeSelfProgress={false}
                  compact={true}
                  showTaskFields={true}
                  values={handleGenerateAssigneeValues(task.assignee)}
                  disable={true}
                  disablePopover={true}
                />
              </div>
            </>
          ) : customizeTiming ? (
            <CustomizedTaskContainer className="flex justify-start items-center gap-3">
              <Popover
                trigger="click"
                overlay={
                  <div>
                    <MultiDatePicker
                      className=""
                      label={t('minStartDate')}
                      id={`overrideTime-${task.id}`}
                      setDate={(value) =>
                        handleChangeOverrideTime(task.id?.toString(), value)
                      }
                      value={overrideTime?.[task.id?.toString()] ?? undefined}
                    />
                  </div>
                }
              >
                <div className="clock" style={{ cursor: 'pointer' }}>
                  <Icon type="clock" color={blue} />
                </div>
              </Popover>
              {!hasChild && (
                <InputWrapper>
                  <label htmlFor={`duration-${task.id}`}>
                    {t('dayNumber')}
                  </label>
                  <input
                    id={`duration-${task.id}`}
                    type="number"
                    min={1}
                    placeholder={t('duration')}
                    value={Math.ceil(
                      (tasksDuration?.[task.id] ??
                        SINGLE_DAY_BASE_ON_TIME_STAMP) /
                        SINGLE_DAY_BASE_ON_TIME_STAMP
                    )}
                    onChange={(event: any) =>
                      handleChangeTaskDuration(
                        task.id.toString(),
                        (Number(event.target.value) || 1) *
                          SINGLE_DAY_BASE_ON_TIME_STAMP
                      )
                    }
                  />
                </InputWrapper>
              )}
            </CustomizedTaskContainer>
          ) : (
            <></>
          )}
        </TaskRowContainer>

        {childrenMap[task.id]?.map((child) =>
          renderTaskTree(
            child,
            childrenMap,
            level + 1,
            dueDate,
            zIndex,
            resolvedProgressByTaskId
          )
        )}
      </div>
    );
  };

  const handleSetDate = (
    task: ITask,
    value: {
      verifyDate?: number | null;
      startDate?: number | null;
      dueDate?: number | null;
    }
  ) => {
    const clonedTask = cloneDeep(task);

    if (value?.verifyDate) {
      clonedTask['verifyDate'] = value.verifyDate;
    } else {
      clonedTask['verifyDate'] = undefined;
    }
    if (value?.startDate) {
      clonedTask['startDate'] = value.startDate;
    } else {
      clonedTask['startDate'] = undefined;
    }
    if (value?.dueDate) {
      clonedTask['dueDate'] = value.dueDate;
    } else {
      clonedTask['dueDate'] = undefined;
    }

    // call handle changedDate
    handleTaskUpdate(clonedTask);
  };

  const handleTaskUpdate = (updatedTask: ITask) => {
    setHierarchyTasks((prev) => {
      if (!prev) return prev;
      return updateTaskInHierarchy(updatedTask, prev);
    });

    const tasks = flatMap(hierarchyTasks).map((el) =>
      el.id === updatedTask.id ? updatedTask : el
    );

    const result = calcDateRange(tasks);
    const normalizedRange: IDiagramTimestampRange = {
      startTime: result.startTime,
      dueTime: result.dueTime,
    };

    setDiagramTimestampRange(normalizedRange);
  };

  const renderTimeLineTree = (
    task: ITask,
    childrenMap: Record<number, ITask[]>,
    level = 0
  ) => {
    const range = diagramTimestampRange
      ? getTaskRangeRatio(task, diagramTimestampRange)
      : null;

    return (
      <div style={{ width: '100%', position: 'relative' }}>
        <Popover
          overlay={
            <div>
              {task.haveStartDate && task.haveDueDate ? (
                <MultiDatePicker_Range
                  isEditable={true}
                  hasTime={task.dateAndTime}
                  label={t('chooseTimeRange')}
                  values={{
                    startDate: task.startDate as number,
                    dueDate: task.dueDate as number,
                  }}
                  setDate={(values) => handleSetDate(task, values)}
                />
              ) : task.haveDueDate ? (
                <MultiDatePicker
                  isEditable={true}
                  hasTime={task.dateAndTime}
                  label={`${task.dueDateTitle ? task.dueDateTitle : t('dueDate')}`}
                  value={task?.dueDate as number}
                  setDate={(value) => {
                    handleSetDate(task, { dueDate: value });
                  }}
                />
              ) : (
                <MultiDatePicker
                  isEditable={true}
                  hasTime={task.dateAndTime}
                  label={`${task.startDateTitle ? task.startDateTitle : t('startDate')}`}
                  value={task?.startDate as number}
                  setDate={(value) => {
                    handleSetDate(task, { startDate: value });
                  }}
                />
              )}
            </div>
          }
          trigger="click"
          placement="bottom"
        >
          <div
            className="flex items-center w-full"
            style={{
              background: exactWhite,
              height: '45px',
              position: 'sticky',
              top: `${level * 42}px`,
              zIndex: 1_000 - level,
              cursor: 'pointer',
            }}
          >
            <ScheduleDurationPoints
              diagramTimestampRange={diagramTimestampRange}
              task={task}
              range={range}
            />
          </div>
        </Popover>
        {childrenMap[task.id]?.map((child) =>
          renderTimeLineTree(child, childrenMap, level + 1)
        )}
      </div>
    );
  };

  const updateTaskInHierarchy = (
    updatedTask: ITask,
    prev: Record<number, ITask[]>
  ): Record<number, ITask[]> => {
    const next: Record<number, ITask[]> = {};

    for (const parentId in prev) {
      next[parentId] = prev[parentId].map((task) =>
        task.id === updatedTask.id ? updatedTask : task
      );
    }

    return next;
  };

  const handleUpdateTask = async () => {
    setSaving(true);
    const updatedTasks = flatMap(hierarchyTasks);
    for (const item of updatedTasks) {
      await appDispatch(
        updateTask({ task: taskManipulate.stateToServer(item) })
      );
    }
    setSaving(false);
    onCloseModal();
  };

  const calcDateRange = (tasks: ITask[]) => {
    const dateRange = tasks?.reduce<IDiagramTimestampRange>(
      (acc: IDiagramTimestampRange, task: ITask) => {
        const taskStart = task?.startDate;
        const taskDue = task?.dueDate;

        return {
          startTime:
            taskStart !== undefined
              ? Math.min(acc.startTime, taskStart)
              : taskDue
                ? Math.min(acc.startTime, taskDue)
                : acc.startTime,

          dueTime:
            taskDue !== undefined
              ? Math.max(acc.dueTime, taskDue)
              : taskStart
                ? Math.max(acc.dueTime, taskStart)
                : acc.dueTime,
        };
      },
      {
        startTime: Number.POSITIVE_INFINITY,
        dueTime: Number.NEGATIVE_INFINITY,
      }
    );

    const safeDateRange = {
      startTime: isFinite(dateRange.startTime)
        ? dateRange.startTime
        : Date.now(),
      dueTime: isFinite(dateRange.dueTime) ? dateRange.dueTime : Date.now(),
    };

    const normalizedRange: IDiagramTimestampRange = {
      startTime: startOfDate(
        new Date(
          new Date(safeDateRange.startTime)?.getFullYear(),
          new Date(safeDateRange.startTime)?.getMonth(),
          new Date(safeDateRange.startTime)?.getDate()
        ),
        'month',
        true,
        'fa'
      ),
      dueTime: endOfDate(
        new Date(
          new Date(safeDateRange.dueTime)?.getFullYear(),
          new Date(safeDateRange.dueTime)?.getMonth(),
          new Date(safeDateRange.dueTime)?.getDate()
        ),
        'month',
        true,
        'fa'
      ),
    };

    return normalizedRange;
  };

  const DFS_Tree_ExpectedProgress_WithAssigneeWeight = (
    taskId: number,
    accWeight: number = 1,
    nodeList: Array<ITask & TNodeWeight & TNodeWeightAssignee> = [],
    level: number = 0
  ) => {
    const currentTask = allStoreTasks[taskId];
    const siblingTasks = Object.values(allStoreTasks || {}).filter(
      (el: ITask) => {
        if (currentTask.parentId === undefined || level === 0) {
          return el.parentId === undefined && el.id === taskId;
        } else {
          return el.parentId === currentTask.parentId;
        }
      }
    );

    const accSiblingTasksWeight = siblingTasks.reduce(
      (acc: number, el: ITask) => {
        acc += el.weight;
        return acc;
      },
      0
    );
    const childrenTasks = Object.values(allStoreTasks || {}).filter(
      (el: ITask) => el.parentId === taskId
    );

    if (
      !childrenTasks?.length ||
      currentTask.taskProgressType.main === 'Assignee'
    ) {
      const nodeWeight =
        accWeight *
        (currentTask.weight / (accSiblingTasksWeight || currentTask.weight));
      return [
        ...nodeList,
        {
          ...currentTask,
          nodeWeight,
          assingneeNodeWeight:
            nodeWeight / (Object.values(currentTask.assignee).length ?? 1),
        },
      ];
    }

    let result = [...nodeList];
    for (const el of childrenTasks) {
      result = DFS_Tree_ExpectedProgress_WithAssigneeWeight(
        el.id,
        (accWeight * currentTask.weight) /
          (accSiblingTasksWeight || currentTask.weight),
        result,
        level + 1
      );
    }

    return result;
  };

  const handleComputeSchedule = () => {
    const taskIdsList = flatMap(hierarchyTasks).map((task) =>
      task.id.toString()
    );

    const relations = taskIdsList.reduce((acc: NodeRelations, taskId) => {
      const curTask = allStoreTasks[Number(taskId)];
      curTask.relatedTasks?.map((rtask) => {
        acc.push([
          taskId,
          'start',
          rtask.taskId.toString(),
          rtask.properties.dependency === 'RequireStart' ? 'start' : 'end',
          0,
        ]);
      });
      return acc;
    }, []);

    const customOverrideTime = Object.fromEntries(
      Object.entries(overrideTime).map(([key, value]) => [
        String(key),
        value ? setStartOfADateBasedOnTimeZone(value) : null,
      ])
    );

    const computedTasksTimeRange = buildAndComputeSchedule({
      baseDate: setStartOfADateBasedOnTimeZone(
        customizedBaseDate ?? new Date().getTime()
      ),
      taskIdsList: taskIdsList,
      childrenDict: Object.fromEntries(
        Object.entries(hierarchyTasks || {}).map(([key, value]) => [
          String(key),
          value?.map((task) => task.id.toString()) || [],
        ])
      ),
      durations: tasksDuration,
      relations: relations,
      customNodeOverrideTime: customOverrideTime,
    });

    const tasks = flatMap(hierarchyTasks);
    tasks.forEach((task: ITask) => {
      if (
        Object.keys(computedTasksTimeRange || {}).includes(task.id.toString())
      ) {
        const updatedTask = {
          ...task,
          startDate: computedTasksTimeRange[task.id.toString()].start,
          dueDate: computedTasksTimeRange[task.id.toString()].end,
        };
        handleTaskUpdate(updatedTask);
      }
    });
    setShowPreview(true);
  };

  const handleIgnoreSchedule = () => {
    setCustomizeTiming(false);
    setShowPreview(false);
    setTasksDuration({});
    setOverrideTime({});

    const allTasksIdList = flatMap(hierarchyTasks).map((el) => el.id);
    const tasks = allTasksIdList.map((id: number) => allStoreTasks[id]);
    tasks.forEach((task: ITask) => {
      handleTaskUpdate(task);
    });
    const result = calcDateRange(tasks);
    const normalizedRange: IDiagramTimestampRange = {
      startTime: result.startTime,
      dueTime: result.dueTime,
    };

    setDiagramTimestampRange(normalizedRange);
  };

  useEffect(() => {
    const getTaskRecursively = async () => {
      setFetchingData(true);
      const finalAns = await getAllTasksRecursively(task.id, []);
      setHierarchyTasks(finalAns ? buildChildrenMap(finalAns, task) : {});

      const dateRange = finalAns?.reduce<IDiagramTimestampRange>(
        (acc: IDiagramTimestampRange, task: ITask) => {
          const taskStart = task?.startDate;
          const taskDue = task?.dueDate;

          return {
            startTime:
              taskStart !== undefined
                ? Math.min(acc.startTime, taskStart)
                : taskDue
                  ? Math.min(acc.startTime, taskDue)
                  : acc.startTime,

            dueTime:
              taskDue !== undefined
                ? Math.max(acc.dueTime, taskDue)
                : taskStart
                  ? Math.max(acc.dueTime, taskStart)
                  : acc.dueTime,
          };
        },
        {
          startTime: Number.POSITIVE_INFINITY,
          dueTime: Number.NEGATIVE_INFINITY,
        }
      );

      if (!dateRange) return;

      const result = calcDateRange(finalAns);
      const normalizedRange: IDiagramTimestampRange = {
        startTime: result.startTime,
        dueTime: result.dueTime,
      };

      setDiagramTimestampRange(normalizedRange);
      setFetchingData(false);
    };

    getTaskRecursively();
  }, []);

  useEffect(() => {
    const fetchTask = async () => {
      appDispatch(
        getSingleTask({
          taskId: task.id,
          includeProgressHistory: true,
          includeAssignmentHistory: true,
        })
      );
    };
    fetchTask();
  }, []);

  useEffect(() => {
    const tasks = flatMap(hierarchyTasks);
    const result = calcDateRange(tasks);
    const normalizedRange: IDiagramTimestampRange = {
      startTime: result.startTime,
      dueTime: result.dueTime,
    };

    setDiagramTimestampRange(normalizedRange);
  }, [hierarchyTasks]);

  useEffect(() => {
    const totalTasks = flatMap(hierarchyTasks);
    const TasksWithTimeRange: IDuration = {};
    totalTasks.forEach((task) => {
      const hasChild = hierarchyTasks?.[task.id]?.length ? true : false;
      if (!hasChild)
        TasksWithTimeRange[task.id] =
          task.dueDate && task.startDate
            ? Math.ceil(
                (task.dueDate - task.startDate < SINGLE_DAY_BASE_ON_TIME_STAMP
                  ? 1
                  : task.dueDate - task.startDate) /
                  SINGLE_DAY_BASE_ON_TIME_STAMP
              ) * SINGLE_DAY_BASE_ON_TIME_STAMP
            : SINGLE_DAY_BASE_ON_TIME_STAMP;
    });
    setTasksDuration(TasksWithTimeRange);
  }, [customizeTiming]);

  const totalTask = flatMap(hierarchyTasks);
  const monthInfo: Array<{ monthName: string; year: string }> = [];
  if (diagramTimestampRange?.startTime || diagramTimestampRange?.dueTime) {
    const startMonthIndex = getJMonthIndex(
      new Date(
        (diagramTimestampRange?.startTime ||
          diagramTimestampRange?.dueTime) as number
      ),
      'fa'
    );
    const dueMonthIndex = getJMonthIndex(
      new Date(
        (diagramTimestampRange?.dueTime ||
          diagramTimestampRange?.startTime) as number
      ),
      'fa'
    );
    for (let index = startMonthIndex; index < dueMonthIndex + 1; index++) {
      const monthIndex = (index % 12) + 1;
      monthInfo.push({
        monthName: getMonthName(monthIndex),
        year: `${moment(
          monthIndexToTimestamp(index, 'fa_IR@calendar=persian')
        ).format('jYYYY')}`,
      });
    }
  }

  const isThereAnyDependantTasks = flatMap(hierarchyTasks).find(
    (el: ITask) => el?.haveRelatedTask
  );

  return (
    <div className="w-full">
      <BlurModal
        isOpen={moreInfoModalIsOpen}
        onCloseModal={() => setMoreInfoModalIsOpen(false)}
        title={t('SmartScheduleTutorial')}
        maxWidth="90vw"
      >
        <MoreInfoModalContent />
      </BlurModal>
      <ButtonGroup
        items={buttonItems}
        onSelect={(itemKey: string) => setSelectedTab(itemKey)}
        selected={selectedTab}
      />
      {tasksInStackId?.taskId && companyId && (
        <ItemTask
          companyId={companyId}
          taskId={tasksInStackId.taskId}
          tab={'info'}
          onClose={handleCloseTaskStackModal}
          disableStack={true}
          zIndex={tasksInStackId.zIndex}
        />
      )}
      {fetchingData ? (
        <LoadingCircleIndicator />
      ) : selectedTab === 'timetable' ? (
        <div className="flex flex-col gap-5">
          <div className="flex justify-end" style={{ padding: '0.25rem' }}>
            {withTitle(
              <Icon
                type="download"
                color={blue}
                style={{ cursor: 'pointer', fontSize: '1.5rem' }}
                onClick={handleExportTimetableExcel}
              />,
              t('excelExport') as string
            )}
          </div>
          <SmartScheduleWrapper>
            <TreeWrapper>
              {hierarchyTasks &&
                renderTaskTree(hierarchyTasks[ROOT_ID][0], hierarchyTasks)}

              {hierarchyTasks &&
                renderTimeLineTree(hierarchyTasks[ROOT_ID][0], hierarchyTasks)}
            </TreeWrapper>

            <TimeRangeVirticalLineWrapper counter={totalTask.length}>
              <div className="absolute" style={{ top: '-5px', width: '100%' }}>
                <table>
                  <TR>
                    {monthInfo?.map((mnInf, ind) => {
                      return (
                        <TH key={ind} num={monthInfo?.length}>
                          <div>{mnInf.monthName}</div>
                          <div>{convertDigitToProperFormat(mnInf.year)}</div>
                        </TH>
                      );
                    })}
                  </TR>
                  {totalTask?.map((_, indx) => {
                    return (
                      <TR key={indx}>
                        {monthInfo?.map((_, ind) => {
                          return <TD key={ind} num={monthInfo.length}></TD>;
                        })}
                      </TR>
                    );
                  })}
                </table>
              </div>
            </TimeRangeVirticalLineWrapper>
          </SmartScheduleWrapper>
          {customizeTiming && (
            <div>
              <MultiDatePicker
                label={t('baseDate') as string}
                setDate={(value) => {
                  setCustomizedBaseDate(value);
                }}
                value={Number(customizedBaseDate)}
                isEditable={true}
                hasTime={false}
              />
            </div>
          )}
          {isThereAnyDependantTasks && (
            <DependantTasksInformationContainer>
              <Icon type="info" color={exactWhite} />
              <div>{t('dependantTasksInformation')}</div>
              <MoreBtn onClick={handleOpenMoreInfoModal}>{t('more')}</MoreBtn>
            </DependantTasksInformationContainer>
          )}
          <div className="flex items-center justify-center gap-5">
            {customizeTiming && (
              <Button onClick={handleIgnoreSchedule} color="black-outline">
                {t('ignore')}
              </Button>
            )}
            {customizeTiming && (
              <Button color="black-outline" onClick={handleComputeSchedule}>
                {t('computeAndPreview')}
              </Button>
            )}
            {!customizeTiming && (
              <Button
                color="black-outline"
                onClick={() => {
                  setCustomizeTiming((prev) => !prev);
                  setCustomizedBaseDate(
                    flatMap(hierarchyTasks).reduce(
                      (current, item) => {
                        if (item.startDate) {
                          if (!current) return item.startDate;
                          return Math.min(item.startDate, current);
                        }
                        if (item.dueDate) {
                          if (!current) return item.dueDate;
                          return Math.min(item.dueDate, current);
                        }
                        return current;
                      },
                      undefined as number | undefined
                    ) || new Date().getTime()
                  );
                }}
              >
                {t('customizeTiming')}
              </Button>
            )}
            <Button
              color="black-outline"
              loading={saving}
              onClick={handleUpdateTask}
            >
              {t('save')}
            </Button>
          </div>
        </div>
      ) : (
        <ProgressWrapper>
          {task.progressHistory?.mainProgress && (
            <ProgressLineChart
              task={task}
              expectedProgressWithAssigneeWeight={DFS_Tree_ExpectedProgress_WithAssigneeWeight(
                task.id
              )}
              TaskTreeComponent={(dueDate: number) =>
                renderTaskTree(
                  task,
                  hierarchyTasks || {},
                  0,
                  dueDate,
                  100_000_000
                )
              }
            />
          )}
        </ProgressWrapper>
      )}
    </div>
  );
};

export default SmartSchedule;
